import React from "react"
import { Outlet, useLoaderData, useLocation, useParams } from "react-router"

import { ModuleErrorBoundary } from "@/components/error-boundary/module-error-boundary"
import {
  useActiveOrganization,
  useAuthentication,
  useAuthorization,
} from "@/contexts"
import type { Organization } from "@/models/organization"
import type { User } from "@/models/user"
import { useDatadogRumSetUser } from "@/services/datadog-rum"
import { useSwitchOrganization } from "@/utils/url"

import type { OrganizationIdLoaderData } from "./organization-id-loader"

const getRedirectOrganizationId = (
  authorizedOrganizations: Organization[],
  organizationIds?: User["organizationIds"]
): string | undefined => {
  const preferredOrganizationId = organizationIds?.[0]?.toString()
  const preferredOrganization = authorizedOrganizations.find(
    (organization) => organization.id === preferredOrganizationId
  )

  return preferredOrganization?.id ?? authorizedOrganizations[0]?.id
}

const isUnauthorizedUrlOrganization = (
  authorizedOrganizations: Organization[],
  authorizedOrganizationsIsFetched: boolean,
  authorizedUrlOrganization?: Organization
): boolean =>
  authorizedOrganizationsIsFetched &&
  !authorizedUrlOrganization &&
  authorizedOrganizations.length > 0

export const OrganizationIdRoute: React.FC = () => {
  const { authorizedOrganizations, authorizedOrganizationsIsFetched } =
    useAuthorization()
  const { activeUser } = useAuthentication()
  const { activeOrganization, setActiveOrganization } = useActiveOrganization()
  const { switchToOrganization } = useSwitchOrganization()
  const { organization: loaderOrganization } =
    useLoaderData<OrganizationIdLoaderData>()
  useDatadogRumSetUser()
  const location = useLocation()
  const params = useParams()

  const urlOrganizationId = params.orgId
  const authorizedUrlOrganization = authorizedOrganizations.find(
    (organization) => organization.id === urlOrganizationId
  )
  const loaderUrlOrganization =
    loaderOrganization?.id === urlOrganizationId ? loaderOrganization : null
  const targetOrganization =
    authorizedUrlOrganization ??
    (authorizedOrganizationsIsFetched ? null : loaderUrlOrganization)

  React.useLayoutEffect(() => {
    if (
      targetOrganization &&
      targetOrganization.id !== activeOrganization?.id
    ) {
      setActiveOrganization(targetOrganization)
    }
  }, [activeOrganization?.id, setActiveOrganization, targetOrganization])

  React.useLayoutEffect(() => {
    if (
      !isUnauthorizedUrlOrganization(
        authorizedOrganizations,
        authorizedOrganizationsIsFetched,
        authorizedUrlOrganization
      )
    ) {
      return
    }

    const redirectOrganizationId = getRedirectOrganizationId(
      authorizedOrganizations,
      activeUser?.organizationIds
    )

    if (!redirectOrganizationId) {
      return
    }

    switchToOrganization(redirectOrganizationId, { replace: true })
  }, [
    activeUser?.organizationIds,
    authorizedOrganizations,
    authorizedOrganizationsIsFetched,
    authorizedUrlOrganization,
    switchToOrganization,
  ])

  if (!targetOrganization || activeOrganization?.id !== urlOrganizationId) {
    return null
  }

  return (
    <ModuleErrorBoundary resetKeys={[location.pathname]}>
      <Outlet />
    </ModuleErrorBoundary>
  )
}
